/**
 * Fracture-System (docs/01, SCOPE_STAGE2 Punkt 3): Spawn in der eigenen Backline
 * (Reihe 3/4 der eigenen Haelfte), Spaltenzonen-Rotation, FIFO-Reparatur.
 */
import type { RunState } from './types'
import { drawInt } from './rngStreams'
import { repairCost } from './economy'

/** absolute Grid-Reihen der eigenen Backline (Reihe 3/4 = die 2 hintersten, A-CC1-Layout) */
export function fractureRows(state: RunState): number[] {
  const rps = state.config.grid.rowsPerSide
  return [rps * 2 - 2, rps * 2 - 1]
}

export function zoneOfCol(state: RunState, col: number): number {
  const zones = state.config.fracture.zones
  for (let i = 0; i < zones.length; i++) {
    const z = zones[i]!
    if (col >= z[0]! && col <= z[1]!) return i
  }
  return zones.length - 1
}

/**
 * +1 Fracture nach 2 Losses. Kandidaten: freie Backline-Felder (ohne Fracture),
 * bevorzugt ohne Unit; Zone != letzte Zone (docs/01). Verdraengte Unit -> Bench.
 * Rueckgabe false nur wenn die gesamte Backline schon fraktuiert ist.
 */
export function spawnFracture(state: RunState): boolean {
  const rows = fractureRows(state)
  const cols = state.config.grid.cols
  interface Cand { col: number; row: number; zone: number; occupied: boolean }
  const all: Cand[] = []
  for (const row of rows) {
    for (let col = 0; col < cols; col++) {
      if (state.fractures.some((f) => f.col === col && f.row === row)) continue
      const occupied = state.champions.some((c) => c.hex && c.hex.col === col && c.hex.row === row)
      all.push({ col, row, zone: zoneOfCol(state, col), occupied })
    }
  }
  if (all.length === 0) return false
  // Zonen-Rotation: nie 2x hintereinander gleiche Zone — ausser es geht nicht anders
  let pool = all.filter((c) => c.zone !== state.lastFractureZone)
  if (pool.length === 0) pool = all
  // unit-freie Felder bevorzugen (A-CC18)
  const freePool = pool.filter((c) => !c.occupied)
  if (freePool.length > 0) pool = freePool
  const pick = pool[drawInt(state.masterSeed, state.rngCounters, 'fracture.spawn', pool.length)]!
  if (pick.occupied) {
    // verdraengte Unit auf die Bench (Bench voll -> Unit bleibt, aber Feld ist geblockt;
    // beim naechsten Deployment-Versuch faellt sie runter — v0-Kante, dokumentiert A-CC18)
    const victim = state.champions.find((c) => c.hex && c.hex.col === pick.col && c.hex.row === pick.row)
    if (victim) {
      const benched = state.champions.filter((c) => c.hex === null).length
      victim.hex = benched < state.config.bench.size ? null : victim.hex
    }
  }
  state.fractures.push({ col: pick.col, row: pick.row, round: state.round, zone: pick.zone })
  state.lastFractureZone = pick.zone
  return true
}

/** Reparatur: max 1/Shop, FIFO (aelteste zuerst), Kosten 5 + floor(Round/10) */
export function repairFracture(state: RunState): boolean {
  if (state.phase !== 'shop') return false
  if (state.shop.repairsUsed >= state.config.fracture.repairPerShop) return false
  if (state.fractures.length === 0) return false
  const cost = repairCost(state.round, state.config)
  if (state.gold < cost) return false
  state.gold -= cost
  state.shop.repairsUsed++
  // FIFO: aelteste (kleinste round, dann Einfuege-Reihenfolge)
  state.fractures.shift()
  return true
}
